/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { ReactNode } from 'react';

import {
  Task,
  TicketCategory,
  TicketQueryResult,
  TicketTypeCreateRequirements,
} from '@totem/components/ticketing/ticketing';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import {
  hasAnyRole,
  isMemberOfAny,
  securityGroupCheckConstraintAny,
} from '@totem/components/userContext/security/userUtilities';
import { SecurityGroupCheckConstraints, UserProfile } from '@totem/types/user';
import { isNotNull, isNull } from '@totem/utilities/common';

export const ticketPermissions: TicketPermissions = {
  projectEditor: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Must be a "Project Manager" on the ticket (Party Involved).
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      ticketData: TicketQueryResult,
      task?: Task,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      const hasAccess =
        isNotNull(ticketData) &&
        isNotNull(ticketData.ticket) &&
        isNotNull(ticketData.ticket.partiesInvolved)
          ? ticketData.ticket.partiesInvolved.findIndex(
              (chk) =>
                chk.email.toLowerCase() === userProfile.email.toLowerCase() &&
                chk.status === 'ACTIVE' &&
                chk.type === 'Project Manager',
            ) >= 0
          : false;

      return hasAccess;
    },
  },
  ticketOwner: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Must be a "Project Manager" or "Requester" on the ticket (Party
        Involved).
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      ticketData: TicketQueryResult,
      task?: Task,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      const hasProjectManager =
        isNotNull(ticketData) &&
        isNotNull(ticketData.ticket) &&
        isNotNull(ticketData.ticket.partiesInvolved)
          ? ticketData.ticket.partiesInvolved.findIndex(
              (chk) =>
                chk.email.toLowerCase() === userProfile.email.toLowerCase() &&
                chk.status === 'ACTIVE' &&
                chk.type === 'Project Manager',
            ) >= 0
          : false;

      const hasTicketTaskEditor =
        isNotNull(ticketData) &&
        isNotNull(ticketData.ticket) &&
        isNotNull(ticketData.ticket.partiesInvolved)
          ? ticketData.ticket.type !== 'Project' &&
            (permissions.internal.hasPermissions(userProfile) ||
              ticketData.ticket.partiesInvolved.findIndex(
                (chk) =>
                  chk.email.toLowerCase() === userProfile.email.toLowerCase() &&
                  chk.status === 'ACTIVE' &&
                  chk.type === 'Requester',
              ) >= 0)
          : false;

      return hasProjectManager || hasTicketTaskEditor;
    },
  },
  taskEditor: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Must be a "Project Manager" on the ticket (Party Involved).
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      ticketData: TicketQueryResult,
      task?: Task,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      const hasProjectManager =
        isNotNull(ticketData) &&
        isNotNull(ticketData.ticket) &&
        isNotNull(ticketData.ticket.partiesInvolved)
          ? ticketData.ticket.partiesInvolved.findIndex(
              (chk) =>
                chk.email.toLowerCase() === userProfile.email.toLowerCase() &&
                chk.status === 'ACTIVE' &&
                chk.type === 'Project Manager',
            ) >= 0
          : false;

      const hasTicketTaskEditor =
        isNotNull(ticketData) &&
        isNotNull(ticketData.ticket) &&
        isNotNull(ticketData.ticket.partiesInvolved)
          ? ticketData.ticket.type !== 'Project' &&
            (permissions.internal.hasPermissions(userProfile) ||
              ticketData.ticket.partiesInvolved.findIndex(
                (chk) =>
                  chk.email.toLowerCase() === userProfile.email.toLowerCase() &&
                  chk.status === 'ACTIVE' &&
                  chk.type === 'Requester',
              ) >= 0)
          : false;

      return hasProjectManager || hasTicketTaskEditor;
    },
  },
  taskStatusChanger: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Must be a "Project Manager" on the ticket (Party Involved).
        <br />
        OR
        <br />
        The "Assigned Resource" on the task.
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      ticketData: TicketQueryResult,
      task?: Task,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      const projectManager =
        isNotNull(ticketData) &&
        isNotNull(ticketData.ticket) &&
        isNotNull(ticketData.ticket.partiesInvolved)
          ? ticketData.ticket.partiesInvolved.findIndex(
              (chk) =>
                chk.email.toLowerCase() === userProfile.email.toLowerCase() &&
                chk.status === 'ACTIVE' &&
                chk.type === 'Project Manager',
            ) >= 0
          : false;

      const assignedResources =
        isNotNull(task) &&
        isNotNull(task.assignedResources) &&
        isNotNull(ticketData) &&
        isNotNull(ticketData.ticket) &&
        isNotNull(ticketData.ticket.partiesInvolved)
          ? ticketData.ticket.partiesInvolved.findIndex(
              (chk) =>
                chk.email.toLowerCase() === userProfile.email.toLowerCase() &&
                chk.status === 'ACTIVE' &&
                task.assignedResources.includes(chk.id),
            ) >= 0
          : false;

      return projectManager || assignedResources;
    },
  },
  categoryPermissions: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Based on Category Configuration.
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      categoryPermissions?: TicketTypeCreateRequirements,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      if (isNull(categoryPermissions)) {
        return true;
      }

      const hasOrgPermissions =
        categoryPermissions.internalOnly === false ||
        permissions.internal.hasPermissions(userProfile);

      const hasRoles =
        categoryPermissions.roles.length === 0 ||
        hasAnyRole(userProfile, categoryPermissions.roles);

      const hasSecurityGroups =
        categoryPermissions.securityGroups.length === 0 ||
        isMemberOfAny(
          userProfile,
          categoryPermissions.securityGroups,
          constraints !== null ? constraints : securityGroupCheckConstraintAny,
        );

      const isOrganizationAllowed =
        isNull(categoryPermissions.organizations) ||
        categoryPermissions.organizations.length === 0 ||
        categoryPermissions.organizations.includes(userProfile.organization.id);

      return (
        hasOrgPermissions &&
        hasRoles &&
        hasSecurityGroups &&
        isOrganizationAllowed
      );
    },
  },
  approvalViewer: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Must be a "Project Manager" or "Assignee" on the ticket (Party
        Involved).
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      ticketData: TicketQueryResult,
      task?: Task,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      const hasSecurityGroups = isMemberOfAny(
        userProfile,
        ['ticket_technician'],
        constraints !== null ? constraints : securityGroupCheckConstraintAny,
      );

      const hasProjectManager =
        isNotNull(ticketData) &&
        isNotNull(ticketData.ticket) &&
        isNotNull(ticketData.ticket.partiesInvolved)
          ? ticketData.ticket.partiesInvolved.findIndex(
              (chk) =>
                chk.email.toLowerCase() === userProfile.email.toLowerCase() &&
                chk.status === 'ACTIVE' &&
                chk.type === 'Project Manager',
            ) >= 0
          : false;

      const hasTicketAssignee =
        isNotNull(ticketData) &&
        isNotNull(ticketData.ticket) &&
        isNotNull(ticketData.ticket.partiesInvolved)
          ? ticketData.ticket.type !== 'Project' &&
            (permissions.internal.hasPermissions(userProfile) ||
              ticketData.ticket.partiesInvolved.findIndex(
                (chk) =>
                  chk.email.toLowerCase() === userProfile.email.toLowerCase() &&
                  chk.status === 'ACTIVE' &&
                  chk.type === 'Assignee',
              ) >= 0)
          : false;

      return hasSecurityGroups || hasProjectManager || hasTicketAssignee;
    },
  },
};

export interface TicketPermissions {
  projectEditor: TicketPermission;
  ticketOwner: TicketPermission;
  taskEditor: TicketPermission;
  taskStatusChanger: TicketPermission;
  categoryPermissions?: CategoryPermissions;
  approvalViewer: TicketPermission;
}

export interface TicketPermission {
  tooltip: ReactNode;
  hasPermissions: (
    userProfile: UserProfile,
    ticketData: TicketQueryResult,
    task?: Task,
    constraints?: SecurityGroupCheckConstraints,
  ) => boolean;
}

export interface CategoryPermissions {
  tooltip: ReactNode;
  hasPermissions: (
    userProfile: UserProfile,
    categoryPermissions?: TicketTypeCreateRequirements,
    constraints?: SecurityGroupCheckConstraints,
  ) => boolean;
}

export const filterCategories = (
  userProfile: UserProfile,
  categoryList: TicketCategory[],
  constraints?: SecurityGroupCheckConstraints,
): TicketCategory[] => {
  if (isNull(categoryList) || categoryList.length === 0) {
    return categoryList;
  }

  for (let idx = 0; idx < categoryList.length; idx++) {
    categoryList[idx].children = filterCategories(
      userProfile,
      categoryList[idx].children,
      constraints,
    );
  }

  const filtered = categoryList.filter((chk) => {
    return ticketPermissions.categoryPermissions.hasPermissions(
      userProfile,
      chk.createRequirements,
      constraints,
    );
  });

  return filtered;
};
